import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useLocationStore } from '@/store/location';
import { AddressSearchModal } from '@/components/common/AddressSearchModal';
import { PageContainer } from '@/components/layout/PageContainer';
import { useGeolocation } from '@/hooks/useGeolocation';

export function AddressSelectPage() {
  const navigate = useNavigate();
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  
  const {
    savedAddresses,
    selectedAddressId,
    selectAddress,
    removeSavedAddress
  } = useLocationStore();
  
  const {
    isSupported: isGeolocationSupported,
    requestPermission,
    getCurrentPosition
  } = useGeolocation();

  // 뒤로가기 버튼
  const handleBack = () => {
    navigate(-1);
  };


  // 주소 검색 모달 열기
  const handleSearchAddress = () => {
    setIsSearchModalOpen(true);
  };

  // 주소 검색 모달에서 주소 선택했을 때
  const handleAddressSelected = (selectedAddress: any) => {
    setIsSearchModalOpen(false);
    // 주소 상세 입력 페이지로 이동
    navigate('/address-detail', { state: { selectedAddress } });
  };

  // 저장된 주소 선택
  const handleSelectSavedAddress = async (addressId: string) => {
    try {
      await selectAddress(addressId);
      // 선택 후 홈으로 돌아가기
      navigate('/');
    } catch (error) {
      console.error('Failed to select address:', error);
      alert('주소 선택에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 주소 삭제
  const handleDeleteAddress = async (e: React.MouseEvent, addressId: string) => {
    e.stopPropagation(); // 부모 버튼 클릭 이벤트 방지
    
    if (confirm('이 주소를 삭제하시겠습니까?')) {
      try {
        await removeSavedAddress(addressId);
      } catch (error) {
        console.error('Failed to delete address:', error);
        alert('주소 삭제에 실패했습니다. 다시 시도해주세요.');
      }
    }
  };

  // 지도에서 위치 찾기 버튼 핸들러
  const handleMapLocationSelect = async () => {
    if (!isGeolocationSupported) {
      alert('이 브라우저에서는 위치 기능을 사용할 수 없습니다.');
      return;
    }

    try {
      setIsRequestingLocation(true);
      
      // 위치 권한 확인
      const permissionState = await requestPermission();
      
      if (permissionState === 'denied') {
        alert('위치 권한이 거부되었습니다.\n브라우저 설정에서 위치 권한을 허용해주세요.');
        return;
      }
      
      // 현재 위치 가져오기
      const position = await getCurrentPosition();
      
      // 지도 페이지로 이동 (현재 위치 정보와 함께)
      navigate('/map-location-select', {
        state: {
          coordinate: {
            latitude: position.latitude,
            longitude: position.longitude
          }
        }
      });
      
    } catch (error) {
      console.error('위치 가져오기 실패:', error);
      
      // 위치 가져오기 실패해도 지도 페이지로 이동 (기본 위치 사용)
      navigate('/map-location-select');
    } finally {
      setIsRequestingLocation(false);
    }
  };

  return (
    <PageContainer hasBottomNav={false}>
      <div className="flex flex-col h-screen bg-white -mx-4 -mt-4">
        {/* Header */}
        <header className="flex items-center justify-between px-4 py-4">
          <button
            onClick={handleBack}
            className="p-0 bg-transparent"
          >
            <img src="/icons/icon_back.svg" alt="뒤로가기" width="24" height="24" />
          </button>
          
          <h1 className="text-[24px] font-bold text-[#222222]">주소 설정</h1>
          
          <div className="w-6" /> {/* Spacer for centering */}
        </header>

        {/* Content */}
        <main className="flex-1 px-4 pb-6">
        {/* 검색 입력창 */}
        <div className="mb-4" style={{ marginBottom: '16px' }}>
          <button
            onClick={handleSearchAddress}
            className="w-full h-8 bg-[#F5F5F5] border border-[#222222] flex items-center px-3 text-left appearance-none outline-none shadow-none"
          >
            <span className="text-[#222222] text-base flex-1">검색어를 입력해주세요</span>
            <img src="/icons/common_home_search_button.svg" alt="검색" width="24" height="24" className="ml-2" />
          </button>
        </div>

        {/* 지도에서 위치 찾기 버튼 */}
        <div style={{ marginBottom: '16px' }}>
          <button
            onClick={handleMapLocationSelect}
            disabled={isRequestingLocation}
            className="w-full bg-[#DEDEDE] rounded-md flex items-center justify-center gap-1 py-1 disabled:opacity-50"
          >
            {isRequestingLocation ? (
              <>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#222222]"></div>
                <span className="text-[20px] font-semibold text-[#222222] font-pretendard">위치 권한 요청 중...</span>
              </>
            ) : (
              <>
                <img src="/icons/icon_gps.svg" alt="GPS" width="32" height="32" />
                <span className="text-[20px] font-semibold text-[#222222] font-pretendard">지도에서 위치 찾기</span>
              </>
            )}
          </button>
        </div>

        {/* 저장된 주소 목록 */}
        <div className="space-y-4" style={{ marginTop: '16px' }}>
          {savedAddresses.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              저장된 주소가 없습니다.
              <br />
              주소를 검색해서 추가해보세요.
            </div>
          ) : (
            savedAddresses.map((address) => (
              <div
                key={address.id}
                className={cn(
                  "w-full p-2.5 rounded-md relative",
                  address.isDefault || selectedAddressId === address.id
                    ? "bg-[#86DB89]"
                    : "bg-[#F5F5F5]"
                )}
              >
                <button
                  onClick={() => handleSelectSavedAddress(address.id)}
                  className="w-full text-left"
                >
                  <div className="flex items-start gap-3">
                    {/* 체크박스 */}
                    <div className={cn(
                      "w-4 h-4 rounded border flex items-center justify-center mt-0.5 flex-shrink-0",
                      address.isDefault || selectedAddressId === address.id
                        ? "bg-white border-white"
                        : "border-[#222222]"
                    )}>
                      {(address.isDefault || selectedAddressId === address.id) && (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path
                            d="M10 3L4.5 8.5L2 6"
                            stroke="#222222"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>

                    {/* 주소 정보 */}
                    <div className="flex-1 min-w-0 pr-8">
                      <div className="text-base font-bold text-[#000000] mb-2">
                        {address.alias}
                      </div>
                      <div className="text-base text-[#000000] break-words">
                        {address.address}
                        {address.detailAddress && ` ${address.detailAddress}`}
                      </div>
                    </div>
                  </div>
                </button>
                
                {/* 삭제 버튼 */}
                <button
                  onClick={(e) => handleDeleteAddress(e, address.id)}
                  className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-[#666666] hover:text-[#222222] transition-colors"
                  aria-label="주소 삭제"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M12 4L4 12M4 4L12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>
        </main>

        {/* 주소 검색 모달 */}
        <AddressSearchModal
          isOpen={isSearchModalOpen}
          onClose={() => setIsSearchModalOpen(false)}
          onAddressSelected={handleAddressSelected}
        />
      </div>
    </PageContainer>
  );
}